import { Alert, Spin, Tooltip } from 'antd';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import apiCalls from 'src/api/apiCalls';
import Loader from 'src/components/loader.component';
import { NumericFormat } from 'react-number-format';
import { LoadingOutlined } from '@ant-design/icons';
import { badge } from 'src/utils/statusColorsCode';

const DISABLED_CARD_STATUSES = new Set([
    "failed", "rejected", "suspended", "inactive", "freeze", "cancelled"
]);


// Helper component for sensitive card details
const CardSensitiveDetails = ({ isHide, viewData }) => {
    const displayedCardNumber = (isHide && viewData?.cardNumber)
        ? `${viewData.cardNumber.substring(0, 4)} XXXX XXXX ${viewData.cardNumber.substring(viewData.cardNumber.length - 4)}`
        : "XXXX XXXX XXXX XXXX";

    const displayedCVV = "***"; // CVV is always masked in this view
    const displayedExpDate = "**/**"; // Expiry date is always masked in this view

    return (
        <>
            <p className='text-2xl font-semibold text-textWhite text-left' style={{ textShadow: '0px 0px 10px rgba(0, 0, 0, 0.6)' }}>
                {displayedCardNumber}
                <button className='btn-plane wh-fit'></button>
            </p>
            <div className='flex items-center justify-between'>
                <div>
                    <p className='text-xs font-normal mb-0 text-textWhite text-left' style={{ textShadow: '0px 0px 10px rgba(0, 0, 0, 0.6)' }}>CVV</p>
                    <p className='md:text-md text-base font-semibold mb-0 text-textWhite' style={{ textShadow: '0px 0px 10px rgba(0, 0, 0, 0.6)' }}>{displayedCVV}</p>
                </div>
                <div>
                    <p className='text-xs font-normal mb-0 text-textWhite text-left' style={{ textShadow: '0px 0px 10px rgba(0, 0, 0, 0.6)' }}>Exp.Date</p>
                    <p className='md:text-md text-base font-semibold mb-0 text-textWhite' style={{ textShadow: '0px 0px 10px rgba(0, 0, 0, 0.6)' }}>{displayedExpDate}</p>
                </div>
                <div></div> {/* Spacer div from original layout */}
            </div>
        </>
    );
};

// Helper component for individual summary items
const SummaryItem = ({ label, value }) => {
    // Do not render if value is effectively empty, to prevent empty paragraphs
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === "")) {
        return null;
    }
    return (
        <div>
            <p className='summary-label w-52'>{label}</p>
            <p className='summary-value'>{value}</p>
        </div>
    );
};

// Helper component for the list of card information
const CardInfoList = ({ cardDetails }) => {
    if (!cardDetails) return null;

    const spendingLimitDisplay = cardDetails.spendingLimit ? (
        <>
            <NumericFormat value={cardDetails.spendingLimit} decimalScale={2} thousandSeparator={true} thousandsGroupStyle="lakh" displayType="text" />
            {` ${cardDetails.currency} / day`}
        </>
    ) : "";

    const atmLimitDisplay = cardDetails.atmDailyWithdrawalLimit ? (
        <NumericFormat value={cardDetails.atmDailyWithdrawalLimit} decimalScale={2} thousandSeparator={true} thousandsGroupStyle='lakh' displayType='text' />
    ) : "";

    return (
        <div className='xl:col-span-2 flex-1 border border-StrokeColor md:p-4 p-2 rounded-5'>
            <div className='grid grid-cols-1 xl:grid-cols-4 md:grid-cols-2 gap-4'>
                <SummaryItem label="Name" value={cardDetails.cardName} />
                <SummaryItem label="Currency Type" value={cardDetails.currency} />
                <SummaryItem label="Consumption Method" value={cardDetails.consumptionMethod} />
                <SummaryItem label="Association" value={cardDetails.association} />
                {cardDetails.cardType === "Physical" && <SummaryItem label="ATM Daily Withdrawal Limit" value={atmLimitDisplay} />}
                <SummaryItem label="Type" value={cardDetails.cardType} />
                <SummaryItem label="Monthly Recharge Limit" value={cardDetails.monthlyRechargeLimit ? <NumericFormat value={cardDetails.monthlyRechargeLimit} decimalScale={2} thousandSeparator={true} thousandsGroupStyle='lakh' displayType='text' /> : ""} />
                <SummaryItem label="Per Transaction Limit" value={cardDetails.perTransactionLimit ? <NumericFormat value={cardDetails.perTransactionLimit} decimalScale={2} thousandSeparator={true} thousandsGroupStyle='lakh' displayType='text' /> : ""} />
                <SummaryItem label="Spending Limit per day" value={spendingLimitDisplay} />
                <SummaryItem label="Status" value={<span className={`status-badge ${badge[cardDetails.cardstatus?.toLowerCase()] || 'bg-green'}`}>{cardDetails.cardstatus || ""}</span>} />
            </div>
        </div>
    );
};

const Cardsview = () => {
    const params = useParams();
    const [cardDetails, setCardDetails] = useState(null);
    const [loader, setLoader] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [isHide, setIsHide] = useState(false);
    const [viewData, setViewData] = useState({})
    const [cardViewLoader, setCardViewLoader] = useState(false)
    const shouldLog = useRef(true)
    useEffect(() => {
        if (shouldLog.current) {
            shouldLog.current = false;
            getCardDetails();
        }
    }, [])
    const getCardDetails = async () => {
        setLoader(true)
        const response = await apiCalls.cardDetails(`CardOverViwDetail`, params?.id)
        if (response.ok) {
            setCardDetails(response.data)
            setLoader(false)
        } else {
            setErrorMsg(apiCalls.isErrorDispaly(response))
            setLoader(false)
        }
    }
    const handleView = async () => {
        if (!isHide) {
            await viewDetails();
        }
        setIsHide(!isHide);
    };
    const viewDetails = async () => {
        setCardViewLoader(true)
        const response = await apiCalls.cardDetails(`CardViwDetail`, params?.id)
        if (response) {
            setCardViewLoader(false)
            setViewData(response?.data)
        }
        else {
            setViewData({})
            setCardViewLoader(false)
        }
    }
    const cardStatusLower = cardDetails?.cardstatus?.toLowerCase();
    const viewDisable = !!(cardStatusLower && DISABLED_CARD_STATUSES.has(cardStatusLower));

    const tooltip = isHide ? "Hide" : "View";
    const tooltipTitle = !viewDisable ? tooltip : "";
    const eyeIcon = !isHide ? 'eye' : 'eye-not'
    const iconClass = viewDisable ? 'disable eye' : eyeIcon;
    const view = useCallback(() => {
        if (!viewDisable) { handleView(); }
    }, [handleView, viewDisable])

    if (loader) {
        return (
            <div className="h-[80vh] flex justify-center items-center mb-5">
                <Loader className="" />
            </div>
        );
    }

    return (
        <div className='mb-5'>
                {errorMsg && <div className='mb-4'>
                    <Alert variant="danger" className='alert-flex' description={errorMsg}
                        showIcon closable>
                        <p style={{ color: 'red', margin: 10 }}><span className="icon error-alert me-2"></span>{errorMsg}</p>
                    </Alert>
                </div>}
                <div className=''>
                    <div className='grid xl:grid-cols-3 md:flex gap-4'>
                        <div className='xl:col-span-1 relative p-3 border border-StrokeColor rounded-5'>
                            <div className='flex gap-4 mb-2 justify-center items-center'>
                                <div className='md:flex items-center flex-1 gap-2'>
                                    <p className='summary-label bg-[transparent] max-sm:pb-0'>Balance</p>
                                    <p className='summary-value p-0 max-sm:px-[10px] max-sm:pb-[10px]'>{cardDetails?.amount || "0"}{" "}{cardDetails?.currency || ""}</p>
                                </div>
                                <div className='text-end w-10 h-10 flex justify-center ml-auto items-center rounded-full border border-cardBorder'>
                                    <Tooltip title={tooltipTitle}>
                                        <span onClick={view} className={`${iconClass} icon cursor-pointer`}></span>
                                    </Tooltip>
                                </div>
                            </div>
                            <div className='card-section relative min-hw'>
                                <img className='card-image w-full' src={cardDetails?.cardImage || "https://digibankstoragespace.blob.core.windows.net/images/admindefault-card.png"} />
                                <div className='mb-4 absolute top-0 left-0 h-full w-full p-4 flex flex-col justify-between cursor-pointer'>
                                    {cardViewLoader && <Spin indicator={<LoadingOutlined spin />} size="small" />}
                                    <div></div>
                                    {!cardViewLoader && <CardSensitiveDetails isHide={isHide} viewData={viewData} />}
                                </div>
                            </div>
                        </div>
                        <CardInfoList cardDetails={cardDetails} />
                    </div>
                </div>
        </div>
    )
}
export default Cardsview